import { ValueGetterFunc } from "@ag-grid-community/core";
import { HStack, Link, Text } from "@chakra-ui/layout";
import { Stat, StatGroup, StatLabel, StatNumber } from "@chakra-ui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { CreditFacilityEventLayout } from "features/credit-facility/CreditFacilityEventLayout";
import {
  PaydownTrancheBreakdownModal,
  PaydownTrancheBreakdownModalProps,
} from "features/credit-facility/CreditFacilityPaydownTrancheBreakdownModal";
import { CreditFacilitySubpageProps } from "features/credit-facility/CreditFacilitySubpageProps";
import { useGetAllocationsByLookupKey } from "features/drawdown/DrawdownUtils";
import {
  getCreditFacilityPaydownTableRetrieveQueryKey,
  getCreditFacilityPrincipalUsesRetrieveQueryKey,
  useCreditFacilityPaydownCreate,
  useCreditFacilityPaydownTableRetrieveSuspense,
  useCreditFacilityRetrieveSuspense,
  useCreditFacilitySummaryRetrieveSuspense,
} from "gen/endpoints/credit-facility/credit-facility";
import { FundFamilyEventSubTypeEnum } from "gen/model/fundFamilyEventSubTypeEnum";
import { FundFamilyUseAllocationTableColumnDataCreditFacilityPaydown } from "gen/model/fundFamilyUseAllocationTableColumnDataCreditFacilityPaydown";
import { FundFamilyUseAllocationTableData } from "gen/model/fundFamilyUseAllocationTableData";
import { FundFamilyUseAllocationTableRow } from "gen/model/fundFamilyUseAllocationTableRow";
import { creditFacilityPaydownCreateBody } from "gen/schemas/credit-facility/credit-facility";
import { cloneDeep, get } from "lodash-es";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useOutletContext } from "react-router-dom";
import Form from "shared/components/form/Form";
import { FormErrorAlert } from "shared/components/form/FormErrorAlert";
import { FundFamilyUseAllocationTable } from "shared/components/fund-family-use-allocation/FundFamilyUseAllocationTable";
import { fundFamilyUseAllocationTableResolver } from "shared/components/fund-family-use-allocation/FundFamilyUseAllocationTableFormUtils";
import {
  FundFamilyUseAllocationTableGetAllocationFn,
  FundFamilyUseAllocationTableRowEventProps,
} from "shared/components/fund-family-use-allocation/FundFamilyUseAllocationTableProps";
import { useGetFundFamilyUseAllocationTableDataGetErrorForCellFnFromResponse } from "shared/components/fund-family-use-allocation/FundFamilyUseAllocationTableUtils";
import {
  PageWizardFormFooter,
  usePageWizard,
} from "shared/components/layout/PageWizard";
import { TableBox } from "shared/components/table/box/TableBox";
import { TableBoxBody } from "shared/components/table/box/TableBoxBody";
import { TableBoxHeader } from "shared/components/table/box/TableBoxHeader";
import { TableBoxStats } from "shared/components/table/box/TableBoxStats";
import { TableBoxTitle } from "shared/components/table/box/TableBoxTitle";
import { useArrayState } from "shared/hooks/useArrayState";
import { useRequiredStringParams } from "shared/hooks/useRequiredStringParams";
import { decimalOrZero, sumDecimalStrings } from "shared/utils/DecimalUtils";
import { setFormValueIfChanged } from "shared/utils/FormUtils";
import {
  formatMoney,
  subtractMoneyStrings,
  sumMoneyStrings,
} from "shared/utils/MoneyUtils";
import { invalidateQueryKeys } from "shared/utils/QueryUtils";
import invariant from "shared/utils/TinyInvariant";

const resolver = fundFamilyUseAllocationTableResolver(
  zodResolver(creditFacilityPaydownCreateBody),
);

export const CreditFacilityPaydownPage: React.FC = () => {
  const { navigateNext } = usePageWizard();
  const { t } = useTranslation(["creditFacility", "common"]);
  const queryClient = useQueryClient();
  const { fundFamilyId, creditFacilityId, fundFamilyEvent } =
    useOutletContext<CreditFacilitySubpageProps>();

  const [trancheModalData, setTrancheModalData] =
    useState<PaydownTrancheBreakdownModalProps | null>(null);
  const { data: creditFacility } = useCreditFacilityRetrieveSuspense(
    fundFamilyId,
    creditFacilityId,
  );

  const { data: allocationTableData } =
    useCreditFacilityPaydownTableRetrieveSuspense(
      fundFamilyId,
      creditFacilityId,
      fundFamilyEvent.id!,
    );

  const {
    mutateAsync: upsertPaydown,
    error: upsertError,
    variables: upsertVariables,
    reset: upsertReset,
  } = useCreditFacilityPaydownCreate({
    mutation: {
      onSuccess: async () =>
        await invalidateQueryKeys(
          queryClient,
          getCreditFacilityPaydownTableRetrieveQueryKey(
            fundFamilyId,
            creditFacilityId,
            fundFamilyEvent.id!,
          ),
          getCreditFacilityPrincipalUsesRetrieveQueryKey(
            fundFamilyId,
            creditFacilityId,
          ),
        ),
    },
  });

  const { value: rows, updateIf } =
    useArrayState<FundFamilyUseAllocationTableRow>(() =>
      cloneDeep(allocationTableData.rows),
    );

  const form = useForm<FundFamilyUseAllocationTableData>({
    defaultValues: { rows: allocationTableData.rows },
    resolver,
  });

  const onRowChange = useCallback(
    ({ row }: FundFamilyUseAllocationTableRowEventProps) =>
      updateIf((oldRow) => (oldRow.id === row.id ? row : null)),
    [updateIf],
  );

  useEffect(() => {
    setFormValueIfChanged(
      form,
      "rows",
      rows.filter(({ fund_family }) => fund_family["paydown"].amount),
      { shouldDirty: true },
    );
  }, [form, rows]);

  const getErrorForCell =
    useGetFundFamilyUseAllocationTableDataGetErrorForCellFnFromResponse(
      upsertError,
      useCallback(
        (rowId) =>
          upsertVariables?.data.rows.findIndex((it) => it.id === rowId),
        [upsertVariables],
      ),
    );

  const onSubmit = async (data: FundFamilyUseAllocationTableData) => {
    await upsertPaydown({
      fundFamilyId,
      creditFacilityId,
      fundFamilyEventId: fundFamilyEvent.id!,
      data,
    });
  };

  const allocationsByLookupKey = useGetAllocationsByLookupKey(
    fundFamilyId,
    useMemo(
      () =>
        rows.map((row) => {
          invariant(row.column_data?.type === "paydown");
          return {
            fund_ids: creditFacility.borrowing_entity_ids,
            transaction_code_id: row.column_data.transaction_code_id || null,
            investment_id: row.column_data.investment_id || null,
          };
        }),
      [rows, creditFacility],
    ),
    fundFamilyEvent.effective_date,
  );

  const getAllocation =
    useCallback<FundFamilyUseAllocationTableGetAllocationFn>(
      (params) => {
        const columnData = params.row.column_data;
        if (columnData?.type === "paydown") {
          const fundAllocations = allocationsByLookupKey?.({
            transaction_code_id: columnData.transaction_code_id || null,
            investment_id: columnData.investment_id || null,
          });
          const fundInfo = fundAllocations?.find(
            (it) => it.id === params.fundDef.fund_id,
          );
          if (fundInfo) {
            return {
              ownershipPercentage: fundInfo.ownershipPercentage,
              allocationRuleId: fundInfo.allocationRuleId,
              allocationRuleName: fundInfo.allocationRuleName,
            };
          }
        }
        return undefined;
      },
      [allocationsByLookupKey],
    );

  // the change stats component expects a negative number for paydown
  const paydownAmount = sumDecimalStrings(
    rows.map((row) => row.fund_family["paydown"].amount),
  )
    .mul(-1)
    .toString();

  const valueGetter = useCallback<
    ValueGetterFunc<FundFamilyUseAllocationTableRow>
  >((params) => {
    const colId = params.column.getColId();
    if (params.node?.data?.column_data) {
      const columnData = params.node.data
        .column_data as FundFamilyUseAllocationTableColumnDataCreditFacilityPaydown;
      if (colId === "outstanding_after_total") {
        const beforeAmount = decimalOrZero(columnData.outstanding_before_total);
        const paydownAmount = decimalOrZero(
          params.node.data.fund_family["paydown"].amount,
        );
        return beforeAmount.sub(paydownAmount).toString();
      } else if (colId.startsWith("outstanding_after_by_fund.")) {
        const fundId = colId.split(".")[1];
        const beforeAmount = decimalOrZero(
          columnData.outstanding_before_by_fund[fundId],
        );
        const paydownAmount = decimalOrZero(
          params.node.data.funds[fundId]["paydown"].amount,
        );
        return beforeAmount.sub(paydownAmount).toString();
      }
    }
    return get(params.data, params.column.getColDef().field!);
  }, []);

  return (
    <CreditFacilityEventLayout canEditEvent={true}>
      <Form
        form={form}
        excludeNonFieldErrors={["rows"]}
        onError={() => true}
        onBeforeSubmit={upsertReset}
        onSubmit={onSubmit}
        onSuccess={navigateNext}
      >
        <FormErrorAlert error={upsertError} mb="16px" />
        <TableBox isFullHeight={true} isFullWidth={true}>
          <TableBoxHeader>
            <TableBoxTitle>{t("creditFacility:paydown.type")}</TableBoxTitle>
          </TableBoxHeader>
          <OutstandingTableChangeStats
            changeAmount={paydownAmount}
            eventType={fundFamilyEvent.sub_type!}
          />
          <TableBoxBody
            sx={{
              '.ag-cell[col-id="use_name"]': {
                fontWeight: "medium",
              },
              '.ag-header-cell[col-id="view_tranches"]': {
                textAlign: "center",
              },
              '.ag-cell[col-id="view_tranches"]': {
                justifyContent: "center",
              },
            }}
          >
            <FundFamilyUseAllocationTable
              tableDef={allocationTableData.definition}
              getAllocation={getAllocation}
              tableData={{ rows }}
              onRowChange={onRowChange}
              context={{ setTrancheModalData }}
              getErrorForCell={getErrorForCell}
              valueGetter={valueGetter}
              columnCellRendererSelector={(columnDef) =>
                columnDef.id === "view_tranches"
                  ? ViewTranchesRenderer
                  : undefined
              }
            />
          </TableBoxBody>
        </TableBox>
        <PageWizardFormFooter />
      </Form>
      {trancheModalData && (
        <PaydownTrancheBreakdownModal
          {...trancheModalData}
          onCloseComplete={() => setTrancheModalData(null)}
        />
      )}
    </CreditFacilityEventLayout>
  );
};

const ViewTranchesRenderer: React.FC<{
  data: FundFamilyUseAllocationTableRow;
  context: {
    setTrancheModalData: React.Dispatch<
      React.SetStateAction<PaydownTrancheBreakdownModalProps | null>
    >;
  };
}> = ({ data, context }) => {
  const { t } = useTranslation(["common"]);
  const columnData =
    data.column_data as FundFamilyUseAllocationTableColumnDataCreditFacilityPaydown;
  return (
    <Link
      colorScheme="gray"
      onClick={() =>
        context.setTrancheModalData({
          useName: columnData.use_name,
          investmentId: columnData.investment_id || "",
          transactionCodeId: columnData.transaction_code_id,
          loanId: columnData.loan_id,
          loanName: columnData.loan_type,
          investmentType: columnData.investment_type || "",
          fundAmounts: Object.entries(data.funds).reduce<
            Record<string, string>
          >(
            (amounts, [fundId, fundAmount]) => ({
              ...amounts,
              [fundId]: fundAmount["paydown"].amount || "0",
            }),
            {},
          ),
        })
      }
    >
      {t("common:view")}
    </Link>
  );
};

export const OutstandingTableChangeStats: React.FC<{
  changeAmount: string;
  eventType: FundFamilyEventSubTypeEnum;
}> = ({ changeAmount, eventType }) => {
  const { t } = useTranslation(["creditFacility", "enums"]);
  invariant(
    eventType === FundFamilyEventSubTypeEnum.paydown ||
      eventType === FundFamilyEventSubTypeEnum.drawdown,
  );
  const { fundFamilyId, creditFacilityId, fundFamilyEventId } =
    useRequiredStringParams();
  const { data: summaryData } = useCreditFacilitySummaryRetrieveSuspense(
    fundFamilyId,
    creditFacilityId,
    {
      fund_family_event_id: fundFamilyEventId,
    },
  );
  const {
    available_amount,
    borrowing_base_amount,
    outstanding_amount,
    unfunded_amount,
  } = summaryData;

  return (
    <TableBoxStats>
      <StatGroup>
        <Stat size="xs" textStyle="bodyBold">
          <StatNumber>{formatMoney(changeAmount)}</StatNumber>
          <StatLabel>
            {t("creditFacility:event_amount", {
              eventType: t(`enums:FundFamilyEventSubTypeEnum.${eventType}`),
            })}
          </StatLabel>
        </Stat>
        <Stat textStyle="bodyBold">
          <StatNumber>
            {Number(changeAmount) ? (
              <HStack>
                <Text textDecoration="line-through">
                  {formatMoney(outstanding_amount)}
                </Text>
                <Text>→</Text>
                <Text>
                  {formatMoney(
                    sumMoneyStrings([outstanding_amount, changeAmount]),
                  )}
                </Text>
              </HStack>
            ) : (
              formatMoney(outstanding_amount)
            )}
          </StatNumber>
          <StatLabel>
            {t("creditFacility:stats.totalPrincipalOutstanding")}
          </StatLabel>
        </Stat>
        <Stat textStyle="bodyBold">
          <StatNumber>{formatMoney(borrowing_base_amount)}</StatNumber>
          <StatLabel>{t("creditFacility:stats.borrowingBaseAmount")}</StatLabel>
        </Stat>
        <Stat textStyle="bodyBold">
          <StatNumber>
            {Number(changeAmount) ? (
              <HStack>
                <Text textDecoration="line-through">
                  {formatMoney(unfunded_amount)}
                </Text>
                <Text>→</Text>
                <Text>
                  {formatMoney(
                    subtractMoneyStrings(unfunded_amount, changeAmount),
                  )}
                </Text>
              </HStack>
            ) : (
              formatMoney(unfunded_amount)
            )}
          </StatNumber>
          <StatLabel>
            {t("creditFacility:stats.unfundedFacilityCommitmentAmount")}
          </StatLabel>
        </Stat>
        <Stat textStyle="bodyBold">
          <StatNumber>
            {Number(changeAmount) ? (
              <HStack>
                <Text textDecoration="line-through">
                  {formatMoney(available_amount)}
                </Text>
                <Text>→</Text>
                <Text>
                  {formatMoney(
                    subtractMoneyStrings(available_amount, changeAmount),
                  )}
                </Text>
              </HStack>
            ) : (
              formatMoney(available_amount)
            )}
          </StatNumber>
          <StatLabel>
            {t("creditFacility:stats.availableBorrowingAmount")}
          </StatLabel>
        </Stat>
      </StatGroup>
    </TableBoxStats>
  );
};
